/**
 * Scale drop form – derived values from rows
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo } from 'react';
import { uniqueKeepOrderStrings } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { resolveRouteId } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import type { Gateway, LeadsList, ShortLinkTld } from '@/hooks';

export function useScaleDropFormDerived(
  rows: DropsWithStatsRow[],
  gateways: Gateway[],
  leadsLists: LeadsList[],
  tlds: ShortLinkTld[],
) {
  const firstRow = rows[0] ?? null;
  const drop = firstRow?.drop ?? null;
  const isMulti = rows.length > 1;

  const routeMatch = useMemo(
    () => (drop?.smsGatewayRoute ? gateways.find((g) => g.name.trim().toLowerCase() === drop!.smsGatewayRoute!.trim().toLowerCase()) ?? null : null),
    [drop?.smsGatewayRoute, gateways],
  );
  const leadsMatch = useMemo(() => {
    if (!drop?.leadsList) return null;
    const name = typeof drop.leadsList === 'string' ? drop.leadsList : drop.leadsList[0] ?? '';
    return leadsLists.find((l) => l.name.trim().toLowerCase() === name.trim().toLowerCase()) ?? null;
  }, [drop?.leadsList, leadsLists]);
  const tldMatch = useMemo(() => {
    const val = drop?.shortLinkTld?.trim?.();
    return !val ? null : tlds.find((t) => t.value === val || t.displayName === val) ?? null;
  }, [drop?.shortLinkTld, tlds]);

  const mergedSendersFromRows = useMemo(() => {
    const all: string[] = [];
    for (const row of rows) {
      const sn = row.drop?.senderName;
      if (Array.isArray(sn)) all.push(...sn);
    }
    return uniqueKeepOrderStrings(all);
  }, [rows]);

  const mergedTextsFromRows = useMemo(() => {
    const all: string[] = [];
    for (const row of rows) {
      const st = row.drop?.smsText;
      if (Array.isArray(st)) all.push(...st);
    }
    return uniqueKeepOrderStrings(all);
  }, [rows]);

  const allDropsHaveGateway = useMemo(
    () => rows.every((r) => resolveRouteId(r.drop?.smsGatewayRoute, gateways) != null),
    [rows, gateways],
  );

  const defaultGatewayForMulti = useMemo(() => {
    for (const row of rows) {
      const id = resolveRouteId(row.drop?.smsGatewayRoute, gateways);
      if (id != null) return id;
    }
    return gateways[0]?.id ?? 0;
  }, [rows, gateways]);

  const defaultStaticTagIdFromRows = useMemo((): string | null | 'unchanged' => {
    if (rows.length === 0) return null;
    if (rows.length === 1) return rows[0]!.drop?.staticTag?.id ?? null;
    return 'unchanged';
  }, [rows]);

  return {
    firstRow,
    drop,
    isMulti,
    routeMatch,
    leadsMatch,
    tldMatch,
    mergedSendersFromRows,
    mergedTextsFromRows,
    allDropsHaveGateway,
    defaultGatewayForMulti,
    defaultStaticTagIdFromRows,
  };
}
